/**
 * Boot order is the whole of §11.5 (plan C4).
 *
 *   1. read the branding cache      — synchronous, no network
 *   2. apply it                     — same tick, so the first paint is correct
 *   3. mount React
 *   4. fetch the authoritative payload and re-apply if `version` changed
 *
 * Steps 1 and 2 happen **before** `createRoot`, at module scope. That is the
 * difference between a repeat visitor seeing their own colours immediately and
 * seeing ours for a frame first. Nothing here awaits the network — a first
 * visit pays one round trip and shows the neutral palette meanwhile, which
 * reads as loading; a repeat visit pays nothing.
 */

import './styles/globals.css';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { App } from './App.js';
import { applyBranding, revealDocument } from './branding/applyBranding.js';
import { readCachedBranding, slugFromLocation } from './branding/brandingCache.js';

/** Longest a user should ever look at a hidden body waiting for branding. */
const REVEAL_FAILSAFE_MS = 1_500;

export function bootstrapBranding(): void {
  const cached = readCachedBranding(window.location.host, slugFromLocation(window.location.search));
  if (cached) {
    applyBranding(cached);
    return;
  }

  // No cache: the neutral palette in index.html is already correct-ish, and
  // BrandingProvider will apply the real one when it arrives. The body stays
  // hidden until then so the neutral palette is not painted as content — but
  // only for as long as the failsafe allows, because a branding outage must
  // never present as a blank page.
  setTimeout(revealDocument, REVEAL_FAILSAFE_MS);
}

bootstrapBranding();

const container = document.getElementById('root');
if (!container) throw new Error('Root container #root is missing from index.html');

createRoot(container).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
